import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Package, 
  AlertTriangle, 
  Search, 
  Printer, 
  Download,
  Filter,
  PackageX,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const InventoryReport = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [reportType, setReportType] = useState('all'); // 'all', 'out_of_stock', 'low_stock'
  const [businessConfig, setBusinessConfig] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productsRes, categoriesRes, configRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/business-config`)
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setBusinessConfig(configRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar productos según criterios
  const getFilteredProducts = () => {
    let filtered = products;

    // Filtrar por tipo de reporte
    // Usar 'existencia' en lugar de 'stock', 'minimo' en lugar de 'stock_minimo'
    if (reportType === 'out_of_stock') {
      filtered = filtered.filter(p => (p.existencia ?? p.stock ?? 0) === 0);
    } else if (reportType === 'low_stock') {
      filtered = filtered.filter(p => {
        const stock = p.existencia ?? p.stock ?? 0;
        const minStock = p.minimo ?? p.stock_minimo ?? 5;
        return stock > 0 && stock <= minStock;
      });
    }

    // Filtrar por categoría
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.categoria === selectedCategory);
    }

    // Filtrar por búsqueda
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.nombre?.toLowerCase().includes(term) ||
        p.codigo_interno?.toLowerCase().includes(term) ||
        (p.codigo_barras && (Array.isArray(p.codigo_barras) ? p.codigo_barras.some(b => b.toLowerCase().includes(term)) : p.codigo_barras.toLowerCase().includes(term)))
      );
    }

    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  // Calcular estadísticas
  // Usar 'existencia' y 'costo' como campos principales, con fallback a 'stock' y 'precio_costo'
  const stats = {
    totalProducts: products.length,
    outOfStock: products.filter(p => (p.existencia ?? p.stock ?? 0) === 0).length,
    lowStock: products.filter(p => {
      const stock = p.existencia ?? p.stock ?? 0;
      const minStock = p.minimo ?? p.stock_minimo ?? 5;
      return stock > 0 && stock <= minStock;
    }).length,
    totalValue: filteredProducts.reduce((sum, p) => {
      const stock = p.existencia ?? p.stock ?? 0;
      const costo = p.costo ?? p.precio_costo ?? 0;
      return sum + (stock * costo);
    }, 0)
  };

  // Imprimir reporte
  const handlePrint = () => {
    const config = businessConfig || { nombre_comercio: 'LICORSTORE RD' };
    const now = new Date();
    const fecha = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
    const hora = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    
    const reportTitle = reportType === 'out_of_stock' 
      ? 'PRODUCTOS SIN EXISTENCIA' 
      : reportType === 'low_stock' 
        ? 'PRODUCTOS CON STOCK BAJO' 
        : 'REPORTE DE INVENTARIO';

    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${reportTitle}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: A4; margin: 15mm; }
            body { 
              font-family: Arial, sans-serif; 
              font-size: 11px;
              line-height: 1.4;
              color: #000;
              background: #fff;
              padding: 10mm;
            }
            .header { 
              text-align: center; 
              border-bottom: 2px solid #000; 
              padding-bottom: 10px; 
              margin-bottom: 15px; 
            }
            .header h1 { font-size: 18px; margin-bottom: 5px; }
            .header h2 { font-size: 14px; font-weight: normal; color: #666; }
            .info { 
              display: flex; 
              justify-content: space-between; 
              margin-bottom: 15px;
              font-size: 10px;
            }
            .stats {
              display: flex;
              justify-content: space-around;
              margin-bottom: 15px;
              padding: 10px;
              background: #f5f5f5;
              border-radius: 5px;
            }
            .stat-item { text-align: center; }
            .stat-value { font-size: 18px; font-weight: bold; color: #333; }
            .stat-label { font-size: 10px; color: #666; }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 10px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 6px 8px; 
              text-align: left;
              font-size: 10px;
            }
            th { 
              background: #333; 
              color: white; 
              font-weight: bold;
            }
            tr:nth-child(even) { background: #f9f9f9; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .out-of-stock { background: #fee2e2 !important; color: #b91c1c; }
            .low-stock { background: #fef3c7 !important; color: #92400e; }
            .footer { 
              margin-top: 20px; 
              text-align: center; 
              font-size: 9px; 
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${config.nombre_comercio || 'LICORSTORE RD'}</h1>
            <h2>${reportTitle}</h2>
          </div>
          
          <div class="info">
            <div>
              <strong>Fecha:</strong> ${fecha} ${hora}<br>
              <strong>Usuario:</strong> ${user?.full_name || 'Admin'}<br>
              <strong>Categoría:</strong> ${selectedCategory === 'all' ? 'Todas' : selectedCategory}
            </div>
            <div style="text-align: right;">
              <strong>Total productos listados:</strong> ${filteredProducts.length}<br>
              <strong>Valor total inventario:</strong> ${formatCurrency(stats.totalValue)}
            </div>
          </div>

          <div class="stats">
            <div class="stat-item">
              <div class="stat-value">${stats.totalProducts}</div>
              <div class="stat-label">Total Productos</div>
            </div>
            <div class="stat-item">
              <div class="stat-value" style="color: #b91c1c;">${stats.outOfStock}</div>
              <div class="stat-label">Sin Existencia</div>
            </div>
            <div class="stat-item">
              <div class="stat-value" style="color: #92400e;">${stats.lowStock}</div>
              <div class="stat-label">Stock Bajo</div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Producto</th>
                <th>Categoría</th>
                <th class="text-center">Stock</th>
                <th class="text-center">Mínimo</th>
                <th class="text-right">Precio Costo</th>
                <th class="text-right">Precio Venta</th>
                <th class="text-right">Valor Stock</th>
              </tr>
            </thead>
            <tbody>
              ${filteredProducts.map(p => {
                const stock = p.existencia ?? p.stock ?? 0;
                const minStock = p.minimo ?? p.stock_minimo ?? 5;
                const costo = p.costo ?? p.precio_costo ?? 0;
                const isOutOfStock = stock === 0;
                const isLowStock = stock > 0 && stock <= minStock;
                const rowClass = isOutOfStock ? 'out-of-stock' : (isLowStock ? 'low-stock' : '');
                
                return `
                  <tr class="${rowClass}">
                    <td>${p.codigo_interno || '-'}</td>
                    <td>${p.nombre}</td>
                    <td>${p.categoria || '-'}</td>
                    <td class="text-center">${stock}</td>
                    <td class="text-center">${minStock}</td>
                    <td class="text-right">${formatCurrency(costo)}</td>
                    <td class="text-right">${formatCurrency(p.precio_venta || 0)}</td>
                    <td class="text-right">${formatCurrency(stock * costo)}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>

          <div class="footer">
            <p>Generado por ${config.nombre_comercio || 'LICORSTORE RD'} - ${fecha} ${hora}</p>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 300);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    toast.success('Generando reporte...');
  };

  // Descargar como CSV
  const handleDownloadCSV = () => {
    const headers = ['Código', 'Producto', 'Categoría', 'Stock', 'Stock Mínimo', 'Precio Costo', 'Precio Venta', 'Valor Stock'];
    const rows = filteredProducts.map(p => {
      const stock = p.existencia ?? p.stock ?? 0;
      const minStock = p.minimo ?? p.stock_minimo ?? 5;
      const costo = p.costo ?? p.precio_costo ?? 0;
      return [
        p.codigo_interno || '',
        `"${p.nombre}"`,
        p.categoria || '',
        stock,
        minStock,
        costo,
        p.precio_venta || 0,
        stock * costo
      ];
    });

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reporte_inventario_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Archivo CSV descargado');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Reporte de Inventario</h1>
          <p className="text-slate-600 mt-1">Productos sin existencia y con stock bajo</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchData}
            data-testid="refresh-btn"
          >
            <RefreshCw size={18} className="mr-2" />
            Actualizar
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDownloadCSV}
            data-testid="download-csv-btn"
          >
            <Download size={18} className="mr-2" />
            Exportar CSV
          </Button>
          <Button 
            onClick={handlePrint}
            className="bg-emerald-500 hover:bg-emerald-600"
            data-testid="print-btn"
          >
            <Printer size={18} className="mr-2" />
            Imprimir
          </Button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div 
          className={`bg-white rounded-xl p-4 shadow-sm border-2 cursor-pointer transition-all ${reportType === 'all' ? 'border-emerald-500 bg-emerald-50' : 'border-transparent hover:border-gray-200'}`}
          onClick={() => setReportType('all')}
          data-testid="filter-all"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Package className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Productos</p>
              <p className="text-2xl font-bold text-slate-800">{stats.totalProducts}</p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-white rounded-xl p-4 shadow-sm border-2 cursor-pointer transition-all ${reportType === 'out_of_stock' ? 'border-red-500 bg-red-50' : 'border-transparent hover:border-gray-200'}`}
          onClick={() => setReportType('out_of_stock')}
          data-testid="filter-out-of-stock"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <PackageX className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-600">Sin Existencia</p>
              <p className="text-2xl font-bold text-red-600">{stats.outOfStock}</p>
            </div>
          </div>
        </div>

        <div 
          className={`bg-white rounded-xl p-4 shadow-sm border-2 cursor-pointer transition-all ${reportType === 'low_stock' ? 'border-amber-500 bg-amber-50' : 'border-transparent hover:border-gray-200'}`}
          onClick={() => setReportType('low_stock')}
          data-testid="filter-low-stock"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <AlertCircle className="text-amber-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-600">Stock Bajo</p>
              <p className="text-2xl font-bold text-amber-600">{stats.lowStock}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-4 shadow-sm border-2 border-transparent">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <AlertTriangle className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-slate-600">Valor del Inventario</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalValue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl p-4 shadow-sm">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Buscar por nombre, código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="w-full md:w-64">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger data-testid="category-select">
                <Filter size={18} className="mr-2 text-gray-400" />
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.nombre}>
                    {cat.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Tabla de productos */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800 text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">CÓDIGO</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">PRODUCTO</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">CATEGORÍA</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">STOCK</th>
                <th className="px-4 py-3 text-center text-sm font-semibold">MÍNIMO</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">P. COSTO</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">P. VENTA</th>
                <th className="px-4 py-3 text-right text-sm font-semibold">VALOR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredProducts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                    <Package size={48} className="mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No hay productos que mostrar</p>
                    <p className="text-sm">Ajusta los filtros para ver más resultados</p>
                  </td>
                </tr>
              ) : (
                filteredProducts.map((product) => {
                  const stock = product.existencia ?? product.stock ?? 0;
                  const minStock = product.minimo ?? product.stock_minimo ?? 5;
                  const costo = product.costo ?? product.precio_costo ?? 0;
                  const isOutOfStock = stock === 0;
                  const isLowStock = stock > 0 && stock <= minStock;
                  
                  return (
                    <tr 
                      key={product.id} 
                      className={`hover:bg-gray-50 ${isOutOfStock ? 'bg-red-50' : isLowStock ? 'bg-amber-50' : ''}`}
                      data-testid={`product-row-${product.id}`}
                    >
                      <td className="px-4 py-3 text-sm font-mono text-gray-600">
                        {product.codigo_interno || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{product.nombre}</div>
                        {product.codigo_barras && (Array.isArray(product.codigo_barras) ? product.codigo_barras.length > 0 : product.codigo_barras) && (
                          <div className="text-xs text-gray-500">Barras: {Array.isArray(product.codigo_barras) ? product.codigo_barras.join(', ') : product.codigo_barras}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {product.categoria || '-'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                          isOutOfStock 
                            ? 'bg-red-100 text-red-800' 
                            : isLowStock 
                              ? 'bg-amber-100 text-amber-800' 
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {stock}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-gray-600">
                        {minStock}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">
                        {formatCurrency(costo)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {formatCurrency(product.precio_venta || 0)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {formatCurrency(stock * costo)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer de la tabla */}
        {filteredProducts.length > 0 && (
          <div className="px-4 py-3 bg-gray-50 border-t flex justify-between items-center">
            <span className="text-sm text-gray-600">
              Mostrando {filteredProducts.length} productos
            </span>
            <span className="text-sm font-medium text-gray-900">
              Valor total: {formatCurrency(stats.totalValue)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryReport;
